import { useEffect, useState } from 'react';

import { apiClient, type SupportedLanguage } from '@/services';
import type { User } from '@/types';
import { Button } from '@/components/ui/button';

// Card generation is written around English as the language being learned — the prompt, the dictionary and
// the exercises all assume it — so the choice is shown but not offered until that stops being true.
const LEARNING_LANGUAGE_CODE = 'en';

const selectClassName =
  'h-9 w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-xs outline-none ' +
  'focus-visible:border-[#2f5c4e] focus-visible:ring-[#2f5c4e]/15 focus-visible:ring-[3px] md:text-sm disabled:opacity-50';

interface UserSettingsFormProps {
  initialNativeLanguage?: string | null;
  initialLearningLanguage?: string | null;
  /** Omitted during onboarding, which asks for languages and nothing else. */
  initialUseSlottedForm?: boolean;
  submitLabel: string;
  savingLabel?: string;
  successMessage?: string;
  onSaved?: (user: User) => void;
}

/**
 * Every account setting in one form: nothing is written until Save is pressed, and Save writes all of it
 * in a single request.
 */
export function UserSettingsForm({
  initialNativeLanguage,
  initialLearningLanguage,
  initialUseSlottedForm,
  submitLabel,
  savingLabel = 'Saving…',
  successMessage,
  onSaved,
}: UserSettingsFormProps) {
  const [languages, setLanguages] = useState<SupportedLanguage[]>([]);
  const [nativeLanguage, setNativeLanguage] = useState(initialNativeLanguage ?? '');
  const [learningLanguage, setLearningLanguage] = useState(initialLearningLanguage ?? '');
  const [useSlottedForm, setUseSlottedForm] = useState(initialUseSlottedForm ?? false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);

  const showsDisplaySettings = initialUseSlottedForm !== undefined;

  useEffect(() => {
    apiClient
      .getSupportedLanguages()
      .then((result) => {
        setLanguages(result);
        setLearningLanguage(
          (current) =>
            current ||
            result.find((l) => l.code === LEARNING_LANGUAGE_CODE)?.code ||
            result[0]?.code ||
            ''
        );
      })
      .catch(() => setError('Could not load supported languages. Please refresh and try again.'));
  }, []);

  const nativeChoices = languages.filter((language) => language.code !== LEARNING_LANGUAGE_CODE);
  const learningChoices = languages.filter((language) => language.code === LEARNING_LANGUAGE_CODE);

  const canSubmit =
    !isSubmitting && !!nativeLanguage && !!learningLanguage && nativeLanguage !== learningLanguage;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) {
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setShowSuccess(false);

    try {
      const updated = await apiClient.updateUserSettings({
        nativeLanguage,
        learningLanguage,
        useSlottedForm: showsDisplaySettings ? useSlottedForm : undefined,
      });
      setShowSuccess(true);
      onSaved?.(updated);
    } catch {
      setError('Could not save your settings. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
      <label className="flex flex-col gap-1.5 text-sm font-medium">
        I already speak
        <select
          className={selectClassName}
          value={nativeLanguage}
          onChange={(event) => setNativeLanguage(event.target.value)}
          disabled={languages.length === 0}
        >
          <option value="" disabled>
            Select your native language
          </option>
          {nativeChoices.map((language) => (
            <option key={language.code} value={language.code}>
              {language.name}
            </option>
          ))}
        </select>
      </label>

      <label className="flex flex-col gap-1.5 text-sm font-medium">
        I want to learn
        <select
          className={selectClassName}
          value={learningLanguage}
          onChange={(event) => setLearningLanguage(event.target.value)}
          disabled={learningChoices.length <= 1}
        >
          <option value="" disabled>
            Select the language you're learning
          </option>
          {learningChoices.map((language) => (
            <option key={language.code} value={language.code}>
              {language.name}
            </option>
          ))}
        </select>
        <span className="text-xs font-normal text-muted-foreground">
          English is the only language available to learn for now.
        </span>
      </label>

      {showsDisplaySettings && (
        <label className="flex cursor-pointer items-center justify-between gap-4">
          <span className="flex flex-col gap-0.5">
            <span className="text-sm font-medium">Show phrase patterns</span>
            <span className="text-sm text-muted-foreground">
              Display cards using their pattern form (hold off on <em>something</em>) instead of the
              exact phrase you saved.
            </span>
          </span>
          <span className="relative inline-flex shrink-0">
            <input
              type="checkbox"
              className="peer sr-only"
              checked={useSlottedForm}
              disabled={isSubmitting}
              onChange={(event) => setUseSlottedForm(event.target.checked)}
            />
            <span
              className="h-6 w-11 rounded-full bg-[#e6e2da] transition-colors peer-checked:bg-[#2f5c4e]
                peer-focus-visible:ring-2 peer-focus-visible:ring-[#2f5c4e] peer-focus-visible:ring-offset-2 peer-disabled:opacity-50"
            />
            <span
              className="pointer-events-none absolute left-0.5 top-0.5 size-5 rounded-full bg-background
                shadow-xs transition-transform peer-checked:translate-x-5"
            />
          </span>
        </label>
      )}

      {nativeLanguage && nativeLanguage === learningLanguage && (
        <p className="text-sm text-destructive">
          Native and learning language must be different.
        </p>
      )}

      {error && <p className="text-sm text-destructive">{error}</p>}

      {showSuccess && successMessage && (
        <p className="text-sm text-emerald-600">{successMessage}</p>
      )}

      <Button
        type="submit"
        disabled={!canSubmit}
        className="h-[42px] rounded-full bg-[#0f1115] text-sm font-bold text-white hover:bg-[#1e1c18] focus-visible:ring-[#2f5c4e]/40"
      >
        {isSubmitting ? savingLabel : submitLabel}
      </Button>
    </form>
  );
}
